/**
 * Direct Mail API client (pin → measure → mail).
 *
 * Everything authenticated goes through the shared `apiClient` from lib/api.ts
 * so Direct Mail inherits the one implementation of JWT refresh, superadmin
 * impersonation and 401/403 handling. The public landing-page calls are the
 * deliberate exception: they use bare `fetch` with no Authorization header, so
 * a signed-out homeowner scanning a QR code is never bounced to /login.
 *
 * Methods return the payload directly (not the `{ data, status }` envelope) and
 * throw on failure. For a message a user can read, pass the thrown error to
 * `getApiErrorMessage` from lib/apiError.ts.
 */

import { apiClient } from './api';
import type {
  AdminMailerPin,
  AdminMailerPinListParams,
  AdminMeasurementOrderListParams,
  CheckSuppressionRequest,
  CheckSuppressionResponse,
  CompleteMeasurementRequest,
  DirectMailStats,
  MailBatch,
  MailBatchCreateRequest,
  MailBatchCreateResponse,
  MailBatchListParams,
  MailCampaign,
  MailCampaignPayload,
  MailCampaignStats,
  MailCampaignStatus,
  MailMeasurementOrder,
  MailMeasurementOrderAdminUpdatePayload,
  MailMeasurementOrderCreateRequest,
  MailProductRate,
  MailProductRateAdmin,
  MailProductRateUpdatePayload,
  MailerPin,
  MailerPinBulkApproveRequest,
  MailerPinBulkApproveResponse,
  MailerPinBulkCreateRequest,
  MailerPinBulkCreateResponse,
  MailerPinBulkQueueRequest,
  MailerPinBulkQueueResponse,
  MailerPinInput,
  MailerPinListParams,
  MailerPinMapDataParams,
  MailerPinMapDataResponse,
  MailerPinMapPoint,
  MailerPinUpdatePayload,
  MailerQueueStats,
  Paginated,
  PublicMailerClaimRequest,
  PublicMailerClaimResponse,
  PublicMailerQuote,
  RenderProofResponse,
  SkipPinRequest,
} from '../types/directMail';

const BASE = '/api/marketing/direct-mail';
const ADMIN_BASE = '/api/marketing/admin/direct-mail';

// ---------------------------------------------------------------------------
// Plumbing
// ---------------------------------------------------------------------------

/** `apiClient` hands back `data: null` on a redirecting 401 — fail loudly instead. */
function unwrap<T>(response: { data: T | null; status: number }): T {
  if (response.data == null) {
    throw new Error(`Request failed (HTTP ${response.status})`);
  }
  return response.data;
}

/** Accepts a bare array too, since a viewset with pagination off returns one. */
function unwrapPaginated<T>(response: { data: unknown; status: number }): Paginated<T> {
  const data = unwrap(response as { data: unknown | null; status: number });
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data as T[] };
  }
  const page = data as Partial<Paginated<T>>;
  return {
    count: page.count ?? page.results?.length ?? 0,
    next: page.next ?? null,
    previous: page.previous ?? null,
    results: page.results ?? [],
  };
}

function unwrapList<T>(response: { data: unknown; status: number }): T[] {
  return unwrapPaginated<T>(response).results;
}

type QueryValue = string | number | boolean | string[] | undefined | null;

function qs(params?: Record<string, QueryValue>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    // `false` is dropped rather than sent: a filter flag reads as "don't filter",
    // and a view that checks truthiness would treat the string "false" as on.
    if (value === undefined || value === null || value === '' || value === false) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => search.append(key, String(entry)));
      return;
    }
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

// ---------------------------------------------------------------------------
// Value helpers
// ---------------------------------------------------------------------------

/**
 * DRF serializes DecimalField as a string, while an ad-hoc dict response
 * renders the same Decimal as a JSON number. Normalize before doing math.
 */
export function moneyToNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** USD formatting for a wire value. `dash` renders empty/null as an em dash. */
export function formatMoney(
  value: string | number | null | undefined,
  options: { decimals?: number; dash?: boolean } = {},
): string {
  const { decimals = 2, dash = false } = options;
  if (dash && (value === null || value === undefined || value === '')) return '—';
  return moneyToNumber(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** "$8,400 – $10,300" for a pin's frozen quote range. */
export function formatQuoteRange(
  low: string | number | null | undefined,
  high: string | number | null | undefined,
): string {
  if (low === null || low === undefined || low === '') return '—';
  return `${formatMoney(low, { decimals: 0 })} – ${formatMoney(high, { decimals: 0 })}`;
}

/** Google Maps needs numbers; the wire may carry either coordinate form. */
export function pinLatLng(pin: Pick<MailerPin, 'latitude' | 'longitude'>): {
  lat: number;
  lng: number;
} {
  return { lat: moneyToNumber(pin.latitude), lng: moneyToNumber(pin.longitude) };
}

export function pinPointLatLng(point: Pick<MailerPinMapPoint, 'lat' | 'lng'>): {
  lat: number;
  lng: number;
} {
  return { lat: moneyToNumber(point.lat), lng: moneyToNumber(point.lng) };
}

/** HTTP status off a thrown api error — claim() answers 409 when someone beat you to it. */
export function apiErrorStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown })?.status;
  return typeof status === 'number' ? status : undefined;
}

/** Route of the public landing page a printed QR ultimately lands on. */
export function mailerLandingPath(publicToken: string): string {
  return `/m/${publicToken}`;
}

/** The URL encoded into the printed QR — a backend 302 into the landing page. */
export function mailerScanUrl(publicToken: string): string {
  return `${window.location.origin}${BASE}/public/scan/${publicToken}/`;
}

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export interface MailCampaignListParams {
  status?: MailCampaignStatus;
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export const mailCampaignsApi = {
  async list(params?: MailCampaignListParams): Promise<Paginated<MailCampaign>> {
    return unwrapPaginated<MailCampaign>(
      await apiClient.get(`${BASE}/campaigns/${qs(params as Record<string, QueryValue>)}`),
    );
  },

  async get(id: number): Promise<MailCampaign> {
    return unwrap(await apiClient.get<MailCampaign>(`${BASE}/campaigns/${id}/`));
  },

  async create(payload: MailCampaignPayload): Promise<MailCampaign> {
    return unwrap(await apiClient.post<MailCampaign>(`${BASE}/campaigns/`, payload));
  },

  async update(id: number, payload: Partial<MailCampaignPayload>): Promise<MailCampaign> {
    return unwrap(await apiClient.patch<MailCampaign>(`${BASE}/campaigns/${id}/`, payload));
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/campaigns/${id}/`);
  },

  async stats(id: number): Promise<MailCampaignStats> {
    return unwrap(await apiClient.get<MailCampaignStats>(`${BASE}/campaigns/${id}/stats/`));
  },
};

// ---------------------------------------------------------------------------
// Pins
// ---------------------------------------------------------------------------

export const mailerPinsApi = {
  async list(params?: MailerPinListParams): Promise<Paginated<MailerPin>> {
    return unwrapPaginated<MailerPin>(
      await apiClient.get(`${BASE}/pins/${qs(params as Record<string, QueryValue>)}`),
    );
  },

  async get(id: number): Promise<MailerPin> {
    return unwrap(await apiClient.get<MailerPin>(`${BASE}/pins/${id}/`));
  },

  async create(pin: MailerPinInput): Promise<MailerPin> {
    return unwrap(await apiClient.post<MailerPin>(`${BASE}/pins/`, pin));
  },

  async update(id: number, payload: MailerPinUpdatePayload): Promise<MailerPin> {
    return unwrap(await apiClient.patch<MailerPin>(`${BASE}/pins/${id}/`, payload));
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/pins/${id}/`);
  },

  /** Duplicates and suppressed addresses come back in `skipped` rather than failing. */
  async bulkCreate(request: MailerPinBulkCreateRequest): Promise<MailerPinBulkCreateResponse> {
    return unwrap(
      await apiClient.post<MailerPinBulkCreateResponse>(`${BASE}/pins/bulk-create/`, request),
    );
  },

  async mapData(params?: MailerPinMapDataParams): Promise<MailerPinMapDataResponse> {
    return unwrap(
      await apiClient.get<MailerPinMapDataResponse>(
        `${BASE}/pins/map-data/${qs(params as Record<string, QueryValue>)}`,
      ),
    );
  },

  async checkSuppression(request: CheckSuppressionRequest): Promise<CheckSuppressionResponse> {
    return unwrap(
      await apiClient.post<CheckSuppressionResponse>(`${BASE}/pins/check-suppression/`, request),
    );
  },

  /** Throws with `status === 409` when another user already claimed the pin. */
  async claim(id: number): Promise<MailerPin> {
    return unwrap(await apiClient.post<MailerPin>(`${BASE}/pins/${id}/claim/`, {}));
  },

  async release(id: number): Promise<MailerPin> {
    return unwrap(await apiClient.post<MailerPin>(`${BASE}/pins/${id}/release/`, {}));
  },

  async completeMeasurement(id: number, request: CompleteMeasurementRequest): Promise<MailerPin> {
    return unwrap(
      await apiClient.post<MailerPin>(`${BASE}/pins/${id}/complete-measurement/`, request),
    );
  },

  async skip(id: number, request: SkipPinRequest): Promise<MailerPin> {
    return unwrap(await apiClient.post<MailerPin>(`${BASE}/pins/${id}/skip/`, request));
  },

  async bulkQueue(request: MailerPinBulkQueueRequest): Promise<MailerPinBulkQueueResponse> {
    return unwrap(
      await apiClient.post<MailerPinBulkQueueResponse>(`${BASE}/pins/bulk-queue/`, request),
    );
  },

  async bulkApprove(request: MailerPinBulkApproveRequest): Promise<MailerPinBulkApproveResponse> {
    return unwrap(
      await apiClient.post<MailerPinBulkApproveResponse>(`${BASE}/pins/bulk-approve/`, request),
    );
  },

  async queueStats(): Promise<MailerQueueStats> {
    return unwrap(await apiClient.get<MailerQueueStats>(`${BASE}/pins/queue-stats/`));
  },
};

// ---------------------------------------------------------------------------
// Measurement orders (ApexOS measures for you — charges the card on file)
// ---------------------------------------------------------------------------

export interface MailMeasurementOrderListParams {
  status?: string;
  page?: number;
  page_size?: number;
}

export const mailMeasurementOrdersApi = {
  async list(params?: MailMeasurementOrderListParams): Promise<Paginated<MailMeasurementOrder>> {
    return unwrapPaginated<MailMeasurementOrder>(
      await apiClient.get(`${BASE}/measurement-orders/${qs(params as Record<string, QueryValue>)}`),
    );
  },

  async get(id: number): Promise<MailMeasurementOrder> {
    return unwrap(await apiClient.get<MailMeasurementOrder>(`${BASE}/measurement-orders/${id}/`));
  },

  /** Org admin only — this charges the saved card. */
  async create(request: MailMeasurementOrderCreateRequest): Promise<MailMeasurementOrder> {
    return unwrap(
      await apiClient.post<MailMeasurementOrder>(`${BASE}/measurement-orders/`, request),
    );
  },
};

// ---------------------------------------------------------------------------
// Batches
// ---------------------------------------------------------------------------

export const mailBatchesApi = {
  async list(params?: MailBatchListParams): Promise<Paginated<MailBatch>> {
    return unwrapPaginated<MailBatch>(
      await apiClient.get(`${BASE}/batches/${qs(params as Record<string, QueryValue>)}`),
    );
  },

  async get(id: number): Promise<MailBatch> {
    return unwrap(await apiClient.get<MailBatch>(`${BASE}/batches/${id}/`));
  },

  async create(request: MailBatchCreateRequest): Promise<MailBatchCreateResponse> {
    return unwrap(await apiClient.post<MailBatchCreateResponse>(`${BASE}/batches/`, request));
  },

  /** Queues the PDF render; poll `get(id)` for `proof_pdf` to appear. */
  async renderProof(id: number): Promise<RenderProofResponse> {
    return unwrap(
      await apiClient.post<RenderProofResponse>(`${BASE}/batches/${id}/render-proof/`, {}),
    );
  },

  /** Org admin only — charges the card and hands the batch to the print provider. */
  async submit(id: number): Promise<MailBatch> {
    return unwrap(await apiClient.post<MailBatch>(`${BASE}/batches/${id}/submit/`, {}));
  },

  async cancel(id: number): Promise<MailBatch> {
    return unwrap(await apiClient.post<MailBatch>(`${BASE}/batches/${id}/cancel/`, {}));
  },
};

// ---------------------------------------------------------------------------
// Rates + ROI
// ---------------------------------------------------------------------------

export const mailRatesApi = {
  async list(): Promise<MailProductRate[]> {
    return unwrapList<MailProductRate>(await apiClient.get(`${BASE}/rates/`));
  },
};

export interface DirectMailStatsParams {
  campaign?: number;
  start_date?: string;
  end_date?: string;
}

export async function getDirectMailStats(params?: DirectMailStatsParams): Promise<DirectMailStats> {
  return unwrap(
    await apiClient.get<DirectMailStats>(
      `${BASE}/stats/${qs(params as Record<string, QueryValue>)}`,
    ),
  );
}

// ---------------------------------------------------------------------------
// Public landing page — unauthenticated on purpose
// ---------------------------------------------------------------------------

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  // No Authorization header, so a 401 can never trigger the shared client's
  // token refresh and /login redirect on a homeowner who has no account.
  // `credentials: 'omit'` also keeps a staff member's session cookie off the
  // request — DRF's SessionAuthentication is in the default list and would
  // then enforce CSRF on the claim POST.
  const response = await fetch(path, {
    ...init,
    credentials: 'omit',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let detail = `Request failed (HTTP ${response.status})`;
    try {
      const body = await response.json();
      detail = body?.error || body?.detail || detail;
    } catch {
      /* non-JSON error body */
    }
    const error = new Error(detail);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

export const publicMailerApi = {
  /** Records a scan as a side effect, so call it once per page load. */
  async getQuote(publicToken: string): Promise<PublicMailerQuote> {
    return publicRequest<PublicMailerQuote>(`${BASE}/public/pin/${publicToken}/`);
  },

  async claim(
    publicToken: string,
    request: PublicMailerClaimRequest,
  ): Promise<PublicMailerClaimResponse> {
    return publicRequest<PublicMailerClaimResponse>(`${BASE}/public/pin/${publicToken}/claim/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
  },

  scanUrl: mailerScanUrl,
  landingPath: mailerLandingPath,
};

// ---------------------------------------------------------------------------
// Superuser fulfillment queue
// ---------------------------------------------------------------------------

export const directMailAdminApi = {
  measurementOrders: {
    async list(params?: AdminMeasurementOrderListParams): Promise<Paginated<MailMeasurementOrder>> {
      return unwrapPaginated<MailMeasurementOrder>(
        await apiClient.get(
          `${ADMIN_BASE}/measurement-orders/${qs(params as Record<string, QueryValue>)}`,
        ),
      );
    },

    async update(
      id: number,
      payload: MailMeasurementOrderAdminUpdatePayload,
    ): Promise<MailMeasurementOrder> {
      return unwrap(
        await apiClient.patch<MailMeasurementOrder>(
          `${ADMIN_BASE}/measurement-orders/${id}/`,
          payload,
        ),
      );
    },

    async complete(id: number): Promise<MailMeasurementOrder> {
      return unwrap(
        await apiClient.post<MailMeasurementOrder>(
          `${ADMIN_BASE}/measurement-orders/${id}/complete/`,
          {},
        ),
      );
    },
  },

  pins: {
    async list(params?: AdminMailerPinListParams): Promise<Paginated<AdminMailerPin>> {
      return unwrapPaginated<AdminMailerPin>(
        await apiClient.get(`${ADMIN_BASE}/pins/${qs(params as Record<string, QueryValue>)}`),
      );
    },
  },

  rates: {
    async list(): Promise<MailProductRateAdmin[]> {
      return unwrapList<MailProductRateAdmin>(await apiClient.get(`${ADMIN_BASE}/rates/`));
    },

    async update(id: number, payload: MailProductRateUpdatePayload): Promise<MailProductRateAdmin> {
      return unwrap(
        await apiClient.patch<MailProductRateAdmin>(`${ADMIN_BASE}/rates/${id}/`, payload),
      );
    },
  },
};

/** One import for the whole org-facing surface. */
export const directMailApi = {
  campaigns: mailCampaignsApi,
  pins: mailerPinsApi,
  measurementOrders: mailMeasurementOrdersApi,
  batches: mailBatchesApi,
  rates: mailRatesApi,
  stats: getDirectMailStats,
  public: publicMailerApi,
  admin: directMailAdminApi,
};

export default directMailApi;

export interface DirectMailPageCatalogEntry {
  key: string;
  label: string;
  required: boolean;
  included: boolean;
}

export interface DirectMailSettings {
  id: number;
  include_detailed_estimate: boolean;
  include_about_us: boolean;
  featured_review_ids: number[];
  include_promotions: boolean;
  promotions_headline: string;
  promotions_text: string;
  include_why_now: boolean;
  why_now_text: string;
  included_pages: string[];
  page_count: number;
  price_per_piece: string | null;
  page_catalog: DirectMailPageCatalogEntry[];
}

export type DirectMailSettingsPayload = Partial<
  Pick<
    DirectMailSettings,
    | 'include_detailed_estimate'
    | 'include_about_us'
    | 'featured_review_ids'
    | 'include_promotions'
    | 'promotions_headline'
    | 'promotions_text'
    | 'include_why_now'
    | 'why_now_text'
  >
>;

export const directMailSettingsApi = {
  /** A draft for one optional page. Returned for review — the server never saves it. */
  async generateCopy(page: string, seed = ''): Promise<{ page: string; draft: string }> {
    return unwrap(
      await apiClient.post<{ page: string; draft: string }>(`${BASE}/settings/generate-copy/`, { page, seed }),
    );
  },
  async get(): Promise<DirectMailSettings> {
    return unwrap(await apiClient.get<DirectMailSettings>(`${BASE}/settings/`));
  },
  async update(payload: DirectMailSettingsPayload): Promise<DirectMailSettings> {
    return unwrap(await apiClient.patch<DirectMailSettings>(`${BASE}/settings/`, payload));
  },
};
